import { useState, useRef, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { User2, X, Search, Receipt } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  appointmentsApi, type Appointment, type AppointmentColor,
  type CreateAppointmentDto, type LeadOption, type ProformaOption,
} from '../../api/appointments'
import { Modal } from '../ui/Modal'
import { DropdownSelect } from '../ui/DropdownSelect'
import { cn } from '../../lib/utils'
import { timeSlots, endTimeOptions, splitLocalDateTime } from '../../lib/scheduling'
import { APPOINTMENT_COLORS, COLOR_ORDER } from './appointmentColors'

interface FormValues {
  title: string
  color: AppointmentColor
  isAllDay: boolean
  allDayDate: string
  startDate: string
  startTime: string
  /** `YYYY-MM-DDTHH:MM` produced by endTimeOptions */
  endAt: string
  description: string
}

interface Props {
  open: boolean
  onClose: () => void
  companyId: string
  prefill?: { start?: string; end?: string; allDay?: boolean }
  editing?: Appointment | null
}

const EMPTY: FormValues = {
  title: '', color: 'primary', isAllDay: false,
  allDayDate: '', startDate: '', startTime: '09:00', endAt: '',
  description: '',
}

export function AppointmentFormModal({ open, onClose, companyId, prefill, editing }: Props) {
  const qc = useQueryClient()
  const [leadQuery, setLeadQuery] = useState('')
  const [leadResults, setLeadResults] = useState<LeadOption[]>([])
  const [leadLoading, setLeadLoading] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null)
  const leadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [proformaOptions, setProformaOptions] = useState<ProformaOption[]>([])
  const [selectedProforma, setSelectedProforma] = useState<ProformaOption | null>(null)
  const proformaTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { register, handleSubmit, watch, setValue, reset, control, formState: { errors } } =
    useForm<FormValues>({ defaultValues: EMPTY })

  const isAllDay = watch('isAllDay')
  const color = watch('color')
  const startDate = watch('startDate')
  const startTime = watch('startTime')

  // Re-seed the form every time the modal opens (new prefill or a different row).
  useEffect(() => {
    if (!open) return
    if (editing) {
      const st = editing.startAt ? splitLocalDateTime(editing.startAt) : { date: '', time: '09:00' }
      const en = editing.endAt ? splitLocalDateTime(editing.endAt) : null
      reset({
        title: editing.title,
        color: editing.color,
        isAllDay: editing.isAllDay,
        allDayDate: editing.allDayDate ?? '',
        startDate: st.date,
        startTime: st.time,
        endAt: en ? `${en.date}T${en.time}` : '',
        description: editing.description ?? '',
      })
      setSelectedLead(editing.lead
        ? { id: editing.lead.id, label: editing.lead.name, phone: editing.lead.phone, email: editing.lead.email }
        : null)
      setSelectedProforma(editing.proforma)
    } else {
      const allDay = prefill?.allDay ?? false
      const start = prefill?.start ? splitLocalDateTime(prefill.start) : null
      const end = prefill?.end && !allDay ? splitLocalDateTime(prefill.end) : null
      reset({
        ...EMPTY,
        isAllDay: allDay,
        allDayDate: allDay ? (prefill?.start?.slice(0, 10) ?? '') : '',
        startDate: start?.date ?? '',
        startTime: start && !allDay ? start.time : '09:00',
        endAt: end ? `${end.date}T${end.time}` : '',
      })
      setSelectedLead(null)
      setSelectedProforma(null)
    }
    setLeadQuery('')
    setLeadResults([])
  }, [open, editing, prefill, reset])

  const createMut = useMutation({
    mutationFn: (dto: CreateAppointmentDto) => appointmentsApi.create(companyId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments', companyId] })
      toast.success('Appointment created')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create appointment'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CreateAppointmentDto }) =>
      appointmentsApi.update(companyId, id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments', companyId] })
      toast.success('Appointment updated')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update appointment'),
  })

  const searchLeads = (q: string) => {
    setLeadQuery(q)
    if (leadTimer.current) clearTimeout(leadTimer.current)
    if (!q.trim()) { setLeadResults([]); return }
    leadTimer.current = setTimeout(async () => {
      setLeadLoading(true)
      try {
        const res = await appointmentsApi.searchLeads(companyId, q.trim())
        setLeadResults(res.data)
      } finally {
        setLeadLoading(false)
      }
    }, 300)
  }

  const pickLead = (lead: LeadOption) => {
    setSelectedLead(lead)
    setLeadQuery('')
    setLeadResults([])
    // Convenience: an untouched title takes the patient's name.
    if (!watch('title').trim()) setValue('title', lead.label)
  }

  // Picking the lead at the top of the form preloads that lead's proformas, so
  // the usual case needs no typing at all. Clearing the lead falls back to the
  // most recent proformas company-wide.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    appointmentsApi.searchProformas(companyId, { leadId: selectedLead?.id })
      .then(r => { if (!cancelled) setProformaOptions(r.data) })
      .catch(() => { if (!cancelled) setProformaOptions([]) })
    return () => { cancelled = true }
  }, [open, companyId, selectedLead?.id])

  // Server-side search: number, or the lead's name / phone / email.
  const searchProformas = (q: string) => {
    if (proformaTimer.current) clearTimeout(proformaTimer.current)
    proformaTimer.current = setTimeout(async () => {
      try {
        const r = await appointmentsApi.searchProformas(companyId, {
          q: q.trim() || undefined,
          // With no query, stay scoped to the picked lead.
          leadId: q.trim() ? undefined : selectedLead?.id,
        })
        setProformaOptions(r.data)
      } catch {
        setProformaOptions([])
      }
    }, 300)
  }

  const onSubmit = (values: FormValues) => {
    // Explicit nulls, not undefined: this form is the full record, so emptying a
    // field here must clear it on the server rather than leave the old value.
    const dto: CreateAppointmentDto = {
      title: values.title,
      leadId: selectedLead?.id ?? null,
      description: values.description ?? '',
      color: values.color,
      isAllDay: values.isAllDay,
      proformaId: selectedProforma?.id ?? null,
    }

    if (values.isAllDay) {
      dto.allDayDate = values.allDayDate
    } else {
      dto.startAt = new Date(`${values.startDate}T${values.startTime}:00`).toISOString()
      dto.endAt = values.endAt ? new Date(`${values.endAt}:00`).toISOString() : null
    }

    if (editing) updateMut.mutate({ id: editing.id, dto })
    else createMut.mutate(dto)
  }

  // Keep the linked proforma in the list even when the current search or the
  // picked lead wouldn't return it, so editing never shows an empty control.
  const proformaChoices = selectedProforma && !proformaOptions.some(p => p.id === selectedProforma.id)
    ? [selectedProforma, ...proformaOptions]
    : proformaOptions

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit appointment' : 'New appointment'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        {/* Lead — first, because most appointments start from a patient */}
        <div>
          <label className="label">Lead</label>
          {selectedLead ? (
            <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
              <User2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 truncate">{selectedLead.label}</p>
                {(selectedLead.phone || selectedLead.email) && (
                  <p className="text-xs text-gray-400 truncate">
                    {[selectedLead.phone, selectedLead.email].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                value={leadQuery}
                onChange={e => searchLeads(e.target.value)}
                className="input pl-8"
                placeholder="Search by name, phone or email..."
              />
              {(leadResults.length > 0 || leadLoading) && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-52 overflow-y-auto">
                  {leadLoading && <div className="px-3 py-2 text-sm text-gray-400">Searching...</div>}
                  {leadResults.map(l => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => pickLead(l)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    >
                      <p className="text-sm text-gray-800 truncate">{l.label}</p>
                      {(l.phone || l.email) && (
                        <p className="text-xs text-gray-400 truncate">
                          {[l.phone, l.email].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">Optional — leave empty to block out time.</p>
        </div>

        {/* Title */}
        <div>
          <label className="label">Title <span className="text-red-500">*</span></label>
          <input
            {...register('title', { required: true })}
            className={cn('input', errors.title && 'border-red-500 focus:ring-red-500')}
            placeholder="Consultation, follow-up visit..."
          />
        </div>

        {/* Colour */}
        <div>
          <label className="label">Colour</label>
          <div className="flex gap-2">
            {COLOR_ORDER.map(c => {
              const spec = APPOINTMENT_COLORS[c]
              const active = color === c
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setValue('color', c)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                    active ? 'border-transparent' : 'border-gray-300 text-gray-600 hover:border-gray-400',
                  )}
                  style={active ? { backgroundColor: spec.hex, color: spec.text } : undefined}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: active ? spec.text : spec.hex }}
                  />
                  {spec.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* All-day toggle */}
        <div className="flex items-center gap-3">
          <Controller
            name="isAllDay"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                onClick={() => field.onChange(!field.value)}
                className={cn(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
                  field.value ? 'bg-primary-600' : 'bg-gray-300',
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200',
                    field.value ? 'translate-x-4' : 'translate-x-0',
                  )}
                />
              </button>
            )}
          />
          <span className="text-sm text-gray-700">All day</span>
        </div>

        {/* When */}
        {isAllDay ? (
          <div>
            <label className="label">Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              {...register('allDayDate', { required: true })}
              className={cn('input', errors.allDayDate && 'border-red-500 focus:ring-red-500')}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="label">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                {...register('startDate', {
                  required: true,
                  // End-time values carry their own date prefix, so a stale one
                  // would submit an end before the new start.
                  onChange: () => setValue('endAt', ''),
                })}
                className={cn('input', errors.startDate && 'border-red-500 focus:ring-red-500')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Start time</label>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <DropdownSelect
                      options={timeSlots(15).map(t => ({ value: t, label: t }))}
                      value={field.value}
                      onChange={v => { field.onChange(v); setValue('endAt', '') }}
                      placeholder="--:--"
                      searchable
                    />
                  )}
                />
              </div>
              <div>
                <label className="label">End time</label>
                <Controller
                  name="endAt"
                  control={control}
                  render={({ field }) => (
                    <DropdownSelect
                      options={endTimeOptions(startDate, startTime)}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="--:--"
                      searchable
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {/* Proforma link + its total (read-only, always mirrors the proforma) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="label">Proforma linked</label>
            <DropdownSelect
              options={proformaChoices.map(p => ({ value: p.id, label: p.label }))}
              value={selectedProforma?.id ?? ''}
              onChange={v => setSelectedProforma(proformaChoices.find(p => p.id === v) ?? null)}
              placeholder={selectedLead ? 'Select a proforma' : 'Search proformas...'}
              searchable
              onSearchChange={searchProformas}
              searchPlaceholder="Lead name, phone, email or number..."
              emptyLabel={selectedLead ? 'No proformas for this lead' : 'No matching proformas'}
              clearLabel="No proforma"
              onClear={() => setSelectedProforma(null)}
              startIcon={<Receipt className="w-4 h-4 text-gray-400" />}
            />
          </div>
          <div>
            <label className="label">Proforma total</label>
            <input
              className="input bg-gray-50"
              value={selectedProforma ? `${Number(selectedProforma.total).toFixed(2)} ${selectedProforma.currency}` : ''}
              placeholder="—"
              readOnly
              tabIndex={-1}
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="label">Notes</label>
          <textarea {...register('description')} rows={3} className="input resize-none" placeholder="Optional notes..." />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" disabled={isPending} className="btn btn-primary">
            {isPending ? 'Saving...' : editing ? 'Save changes' : 'Create appointment'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
